import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from '@/components/Ad/AuthorInfo/AuthorInfo.styles';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { PhoneNumbers } from '@/components/PhoneNumbers/PhoneNumbers';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import {
  buildWorkingScheduleString,
  getUsersAdsHref,
} from '@/components/Ad/AuthorInfo/AuthorInfo.helpers';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useAdDetails } from '@/hooks/useAdDetails';
import { AdStatus } from '@/types';
import { useIsAuthor } from '@/hooks/useIsAuthor';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useShopFavorite } from '@/hooks/useShopFavorite';
import { useServerDataContext } from '@/contexts';

export const AuthorInfo = () => {
  const isBrowser = typeof window !== 'undefined';
  const { t } = useTranslation();
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const isAuthor = useIsAuthor();
  const { categoryDetails } = useCurrentCategoryDetails({
    skip: !isBrowser,
  });

  const { isFavorited: isFavoritedLocal, toggleFavorite: handleSubscribe } =
    useShopFavorite({ shop: adData?.shop ?? undefined });

  if (!adData) {
    return (
      <S.AuthorInfoBox>
        <SkeletonLoader
          dataTestId="author-info-skeleton-loader"
          borderRadius={14}
          height="125px"
        />
      </S.AuthorInfoBox>
    );
  }

  const { shop, contact, id, status, user } = adData;

  const isInfoVisible = isAuthor || status === AdStatus.Published;

  const isWarningVisible =
    !isAuthor &&
    !shop &&
    (status === AdStatus.Published || status === AdStatus.Expired);

  const info = {
    name: shop ? shop.name : contact.name,
    avatar: shop ? shop.logo?.url : contact.logo?.url,
    phones: contact.phones,
  };

  const usersAdsHref =
    !shop && user?.publishedAdsCount > 1 && getUsersAdsHref(user.legacyId);

  return (
    <S.AuthorInfoBox>
      {isInfoVisible && (
        <>
          <S.Main
            {...(shop?.uri
              ? {
                  href: shop.uri,
                  as: CustomLink,
                  dataTestId: 'author-info-main',
                }
              : { as: 'div', 'data-testid': 'author-info-main' })}
          >
            <S.MainInfoContainer>
              <S.AuthorName $variant="1" data-testid="author-info-name">
                {info.name}
              </S.AuthorName>
              {!!shop && (
                <S.WorkingHours data-testid="author-info-working-schedule">
                  {buildWorkingScheduleString(shop)}
                </S.WorkingHours>
              )}
              {shop?.uri && (
                <S.Link $variant="3" data-testid="author-info-link">
                  {t('pages.ad.users_ads')}
                </S.Link>
              )}
              {usersAdsHref && (
                <S.Link
                  $variant="3"
                  data-testid="author-info-link"
                  as={CustomLink}
                  href={usersAdsHref}
                >
                  {t('pages.ad.users_ads')}
                </S.Link>
              )}
            </S.MainInfoContainer>
            {info.avatar ? (
              <S.Avatar
                data-testid={AdPageTestIds.authorInfo.avatar}
                src={info.avatar}
                alt={info.name}
                width={50}
                height={50}
                loading="lazy"
              />
            ) : (
              <IconBox
                data-testid={AdPageTestIds.authorInfo.defaultAvatar}
                $filename="profile.svg"
                $iconWidth={50}
                $iconHeight={50}
                $width={50}
                $height={50}
              />
            )}
          </S.Main>
          {shop && (
            <S.AddShopFavorite
              $isFavoritedLocal={isFavoritedLocal}
              onClick={handleSubscribe}
            >
              {isFavoritedLocal
                ? t('shop.added_bookmark_shop')
                : t('shop.bookmark_shop')}
            </S.AddShopFavorite>
          )}
          <PhoneNumbers phones={info.phones} adId={id} />
        </>
      )}

      {isWarningVisible && categoryDetails?.warningMessage && (
        <S.Warning data-testid={AdPageTestIds.authorInfo.warning}>
          <IconBox $filename="warning-3.svg" $width={30} $height={30} />
          <Typography.Body
            $variant="4"
            data-testid={AdPageTestIds.authorInfo.warningText}
          >
            <S.WarningTitle $variant="4" as="span">
              {t('common.attention')}{' '}
            </S.WarningTitle>
            {categoryDetails?.warningMessage}
          </Typography.Body>
        </S.Warning>
      )}
    </S.AuthorInfoBox>
  );
};
